import { Link } from "react-router-dom";
import { ArrowRight, Infinity as InfinityIcon, BadgeCheck, TrendingUp, Inbox, MessageSquare, MapPin, ShieldCheck, Sparkles, ClipboardList, Rocket, Check, X, Handshake } from "lucide-react";
import { SpotsMeter, useSpots } from "@/components/site/SpotsMeter";
import { useSeo } from "@/lib/seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PERKS = [
  { icon: InfinityIcon, t: "Free listing for life", d: "Founding members never pay for their directory listing — not at launch, not after." },
  { icon: BadgeCheck, t: "Founding member badge", d: "A permanent badge on your listing that tells investors you were here from day one." },
  { icon: TrendingUp, t: "Priority placement at launch", d: "Founding firms are shown above later listings in the directory, state and city pages." },
];

const FEATURES = [
  { icon: ShieldCheck, t: "A reviewed profile", d: "Logo, bio, coverage, exchange types and safeguards like bonding and E&O — all up front." },
  { icon: Inbox, t: "Investor lead inbox", d: "Inquiries arrive with property value and exchange timeline so you can prioritize." },
  { icon: MessageSquare, t: "Private QI network", d: "Message other intermediaries for referrals, overflow and out-of-state help." },
  { icon: MapPin, t: "State & city pages", d: "Appear on dedicated pages for every state and the metros you serve." },
];

const STEPS = [
  { icon: Sparkles, t: "Create your account", d: "Sign up with Google or email in under a minute." },
  { icon: ClipboardList, t: "Complete the intake", d: "Tell us about your firm, coverage and goals. Your founding spot is held once you submit." },
  { icon: Rocket, t: "Go live at launch", d: "We review every listing. Approved founding firms are featured when the directory opens to investors." },
];

const FAQS = [
  ["Is it really free?", "Yes. The first 30 qualified intermediaries who complete the intake get a free listing for life."],
  ["When is my spot counted?", "When you submit the intake form. Creating an account alone doesn't hold a spot."],
  ["What happens after 30 firms join?", "New intermediaries can join the waitlist for our upcoming paid plan."],
  ["Who reviews the listings?", "Every listing is reviewed before it's published so investors only see real, qualified intermediaries."],
  ["Can I talk to other QIs before launch?", "Yes. Once you've completed your intake you can browse other founding firms and message them in the 1031 Club chat."],
  ["I'm a CPA, attorney or broker. Can I join?", "Yes. Referral partners join the 1031 Club free. Once approved you can chat with QIs and send them client referrals. Partners don't use one of the 30 QI spots."],
];

const CTA = ({ full, testid, className = "" }) => full
  ? <Link to="/waitlist" className={`btn-pink ${className}`} data-testid={testid}>Join the paid plan waitlist <ArrowRight size={16} /></Link>
  : <Link to="/login?mode=register" className={`btn-pink ${className}`} data-testid={testid}>Claim my founding spot <ArrowRight size={16} /></Link>;

const Hero = ({ full }) => (
  <section className="relative overflow-hidden grain">
    <img src="/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-gradient-to-b from-ink/90 via-ink/80 to-ink" />
    <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] bg-qi-pink/20 rounded-full blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-28 grid lg:grid-cols-12 gap-12 items-end">
      <div className="lg:col-span-8">
        <h1 data-testid="landing-hero-title" className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.02] mt-5 rise d1">
          Get found, get leads and do more exchanges. <span className="text-qi-pink">Free for life.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-white/65 max-w-2xl leading-relaxed rise d2">
          Join the directory where Investors are actively seeking a QI for their next 1031 exchange. Claim one of 30 free founding spots before the doors open.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 rise d3">
          <CTA full={full} testid="landing-hero-cta-button" />
          <a href="#perks" className="btn-ghost" data-testid="landing-hero-perks-link">See founding perks</a>
        </div>
      </div>
      <div className="lg:col-span-4 rise d4"><SpotsMeter /></div>
    </div>
  </section>
);

const COMPARE = [
  ["Cost", "$2,000-a-year membership fee", "$0 — free for life for founding members"],
  ["Your listing", "A directory entry", "A full profile: logo, bio, credentials, bonding & coverage"],
  ["Investor leads", "Investors look up your contact details", "Inquiries land in your inbox with property value & timeline"],
  ["Networking", "Scheduled events & conferences", "Always-on private chat with QIs & referral partners"],
  ["Local visibility", "Search by company name", "Dedicated pages for every state and top metro"],
];

const Club = ({ full }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24" data-testid="landing-club">
    <div className="relative overflow-hidden rounded-[40px] border border-qi-pink/30 bg-gradient-to-br from-qi-pink/15 via-ink-2 to-ink p-5 sm:p-8 md:p-14">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-qi-pink/20 rounded-full blur-3xl pointer-events-none" />
      <div className="relative grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="eyebrow">The 1031 Club</span>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-4 leading-[1.02]" data-testid="landing-club-title">Join the 1031 Club.</h2>
          <p className="text-white/75 mt-5 text-lg leading-relaxed">A private network of Qualified Intermediaries and referral partners.</p>
          <p className="text-white/75 mt-3 text-lg leading-relaxed">No $2,000-a-year fee like old legacy listings <span aria-label="wink" role="img">😉</span></p>
          <p className="font-display text-2xl font-black text-qi-pink mt-6">We'll help you win!</p>
          <div className="mt-8"><CTA full={full} testid="landing-club-cta-button" /></div>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-12 gap-3 px-4 pb-3 font-mono text-[11px] uppercase tracking-wider text-white/40">
            <span className="col-span-3" />
            <span className="col-span-4">Legacy listings</span>
            <span className="col-span-5 text-qi-pink">The 1031 Club</span>
          </div>
          <div className="space-y-2" data-testid="landing-compare-table">
            {COMPARE.map(([k, old, ours]) => (
              <div key={k} className="grid grid-cols-12 gap-3 items-center rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-4">
                <span className="col-span-12 sm:col-span-3 text-xs font-bold uppercase tracking-wider text-white/60">{k}</span>
                <span className="col-span-5 sm:col-span-4 text-sm text-white/40 inline-flex items-start gap-2"><X size={14} className="mt-0.5 shrink-0 text-white/30" />{old}</span>
                <span className="col-span-7 sm:col-span-5 text-sm text-white font-semibold inline-flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-qi-emerald" />{ours}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PartnerStrip = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8" data-testid="landing-partner-strip">
    <div className="glass-card p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-qi-emerald/15 text-qi-emerald flex items-center justify-center shrink-0"><Handshake size={22} /></div>
        <div>
          <p className="font-display text-lg font-bold">CPA, attorney or broker?</p>
          <p className="text-sm text-white/55">Join the 1031 Club as a referral partner — free. Chat with QIs and send them client referrals.</p>
        </div>
      </div>
      <Link to="/login?mode=register&type=partner" className="btn-ghost shrink-0" data-testid="landing-partner-cta-button">Join as a partner <ArrowRight size={16} /></Link>
    </div>
  </section>
);

const Perks = () => (
  <section id="perks" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 scroll-mt-20" data-testid="landing-perks">
    <span className="eyebrow">Founding perks</span>
    <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight max-w-2xl">What the first 30 firms get — permanently.</h2>
    <div className="grid md:grid-cols-3 gap-5 mt-12">
      {PERKS.map((p, i) => (
        <div key={p.t} className={`relative overflow-hidden rounded-[28px] p-8 ${i === 0 ? "bg-qi-pink text-white" : "glass-card"}`}>
          <p.icon size={28} className={i === 0 ? "text-white" : "text-qi-pink"} />
          <h3 className="font-display text-2xl font-black mt-6">{p.t}</h3>
          <p className={`text-sm mt-3 leading-relaxed ${i === 0 ? "text-white/85" : "text-white/55"}`}>{p.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Features = () => (
  <section className="border-y border-white/10 bg-white/[0.02]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <span className="eyebrow">What you get</span>
        <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight">More than a name on a list.</h2>
        <p className="text-white/55 mt-4 leading-relaxed">Legacy directories give you a line of text. Founding members get a full profile, a lead inbox and a private network of peers.</p>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
        {FEATURES.map((f) => (
          <div key={f.t} className="glass-card p-7 hover:border-qi-pink/40 transition-colors">
            <div className="w-12 h-12 rounded-2xl bg-qi-pink/15 text-qi-pink flex items-center justify-center"><f.icon size={22} /></div>
            <h3 className="font-display text-lg font-bold mt-5">{f.t}</h3>
            <p className="text-sm text-white/55 mt-2 leading-relaxed">{f.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
    <span className="eyebrow">How to join</span>
    <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight">Three steps. About five minutes.</h2>
    <div className="grid md:grid-cols-3 gap-5 mt-12">
      {STEPS.map((s, i) => (
        <div key={s.t} className="glass-card p-8">
          <div className="flex items-center justify-between">
            <s.icon className="text-qi-pink" size={24} />
            <span className="font-mono text-xs text-white/30">0{i + 1}</span>
          </div>
          <h3 className="font-display text-xl font-bold mt-6">{s.t}</h3>
          <p className="text-sm text-white/55 mt-2 leading-relaxed">{s.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Faq = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-12 gap-10" data-testid="landing-faq">
    <div className="lg:col-span-4">
      <span className="eyebrow">Questions</span>
      <h2 className="font-display text-3xl font-black mt-4 tracking-tight">Before you claim a spot</h2>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-8 glass-card px-6">
      {FAQS.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-white/10 last:border-0">
          <AccordionTrigger className="text-left font-display text-base font-bold hover:no-underline" data-testid={`landing-faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-white/60 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const FinalCta = ({ full, spots }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10">
    <div className="relative overflow-hidden rounded-[40px] bg-white text-ink p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-qi-pink/20 rounded-full blur-3xl" />
      <div className="md:col-span-8 relative">
        <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold text-qi-pink">{full ? "Founding roster full" : `${spots?.remaining ?? ""} spots left`}</span>
        <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight mt-4">{full ? "Get first access to the paid plan." : "Be on the directory from day one."}</h2>
        <p className="mt-4 text-slate-600 max-w-xl">{full ? "All 30 founding spots are claimed. Join the waitlist and we'll reach out when paid listings open." : "Lock in a free listing for life, a founding badge and priority placement before we open to investors."}</p>
      </div>
      <div className="md:col-span-4 relative flex md:justify-end"><CTA full={full} testid="landing-final-cta-button" className="!px-8 !py-4 shadow-xl shadow-qi-pink/30" /></div>
    </div>
  </section>
);

export default function Landing() {
  const spots = useSpots();
  const full = spots?.remaining === 0;
  useSeo({
    title: "Founding QI Members — Free Listing for Life | Find a Qualified Intermediary",
    description: "The first 30 1031 qualified intermediaries get a free listing for life, a founding member badge and priority placement when the directory launches.",
  });
  return (
    <div data-testid="landing-page">
      <Hero full={full} />
      <Club full={full} />
      <PartnerStrip />
      <Perks />
      <Features />
      <Steps />
      <Faq />
      <FinalCta full={full} spots={spots} />
    </div>
  );
}
